import { useState, useRef } from 'react'
import ReactMarkdown from 'react-markdown'

// Inserts markdown syntax at cursor or wraps selection.
// Returns { newText, newStart, newEnd } — caller applies to textarea.
function computeInline(text, selStart, selEnd, before, after, placeholder) {
  const selected = text.substring(selStart, selEnd)
  if (selected) {
    const newText = text.substring(0, selStart) + before + selected + after + text.substring(selEnd)
    return { newText, newStart: selStart + before.length, newEnd: selEnd + before.length }
  }
  const insert = before + placeholder + after
  const newText = text.substring(0, selStart) + insert + text.substring(selStart)
  return { newText, newStart: selStart + before.length, newEnd: selStart + before.length + placeholder.length }
}

// Prefixes each selected line with a string (for headings, lists, blockquote).
function computeLinePrefix(text, selStart, selEnd, prefix) {
  const lineStart = text.lastIndexOf('\n', selStart - 1) + 1
  const lineEnd   = text.indexOf('\n', selEnd)
  const end       = lineEnd === -1 ? text.length : lineEnd
  const lines     = text.substring(lineStart, end).split('\n')
  const replaced  = lines.map(l => prefix + l).join('\n')
  const newText   = text.substring(0, lineStart) + replaced + text.substring(end)
  const delta     = prefix.length * lines.length
  return { newText, newStart: selStart + prefix.length, newEnd: selEnd + delta }
}

function applyInsert(textareaEl, fireChange, before, after = '', placeholder = '', linePrefix = false) {
  const { value, selectionStart: s, selectionEnd: e } = textareaEl
  const { newText, newStart, newEnd } = linePrefix
    ? computeLinePrefix(value, s, e, before)
    : computeInline(value, s, e, before, after, placeholder)
  fireChange(newText)
  // Restore focus + cursor after React re-render
  requestAnimationFrame(() => {
    textareaEl.focus()
    textareaEl.setSelectionRange(newStart, newEnd)
  })
}

const TOOLBAR_ACTIONS = [
  { label: 'B',     title: 'Bold (Cmd+B)',        fn: (el, fc) => applyInsert(el, fc, '**', '**', 'texto') },
  { label: 'I',     title: 'Italic (Cmd+I)',       fn: (el, fc) => applyInsert(el, fc, '*',  '*',  'texto') },
  { label: 'H1',    title: 'Heading 1',            fn: (el, fc) => applyInsert(el, fc, '# ',  '', '', true) },
  { label: 'H2',    title: 'Heading 2',            fn: (el, fc) => applyInsert(el, fc, '## ', '', '', true) },
  { label: 'H3',    title: 'Heading 3',            fn: (el, fc) => applyInsert(el, fc, '### ','', '', true) },
  { label: '`',     title: 'Inline code',          fn: (el, fc) => applyInsert(el, fc, '`',   '`', 'code') },
  { label: '```',   title: 'Code block',           fn: (el, fc) => applyInsert(el, fc, '```\n', '\n```', 'code') },
  { label: '—',     title: 'Unordered list',       fn: (el, fc) => applyInsert(el, fc, '- ',  '', '', true) },
  { label: '1.',    title: 'Ordered list',         fn: (el, fc) => applyInsert(el, fc, '1. ', '', '', true) },
  { label: '>',     title: 'Blockquote',           fn: (el, fc) => applyInsert(el, fc, '> ',  '', '', true) },
  { label: 'link',  title: 'Link (Cmd+K)',         fn: (el, fc) => applyInsert(el, fc, '[', '](url)', 'texto') },
]

const SHORTCUTS = { b: 0, i: 1, k: 10 } // index into TOOLBAR_ACTIONS

function Toolbar({ textareaRef, value, onInsert }) {
  return (
    <div className="flex flex-wrap gap-0.5 px-2 py-1.5 bg-zinc-800/80 border border-b-0 border-zinc-700 rounded-t">
      {TOOLBAR_ACTIONS.map(({ label, title, fn }) => (
        <button
          key={label}
          type="button"
          title={title}
          onMouseDown={e => {
            e.preventDefault() // keep textarea focus
            if (textareaRef.current) fn(textareaRef.current, onInsert)
          }}
          className="px-2 py-0.5 text-xs font-mono text-zinc-400 hover:text-emerald-400 hover:bg-zinc-700 rounded transition-colors"
        >
          {label}
        </button>
      ))}
    </div>
  )
}

const PROSE = [
  'text-sm font-mono leading-relaxed text-zinc-300',
  '[&_h1]:text-zinc-100 [&_h1]:font-bold [&_h1]:text-lg [&_h1]:mb-2',
  '[&_h2]:text-zinc-100 [&_h2]:font-semibold [&_h2]:text-base [&_h2]:mb-1',
  '[&_h3]:text-zinc-200 [&_h3]:font-semibold',
  '[&_strong]:text-zinc-100',
  '[&_code]:bg-zinc-700 [&_code]:px-1 [&_code]:rounded [&_code]:text-emerald-400 [&_code]:text-xs',
  '[&_pre]:bg-zinc-700 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-2',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-0.5',
  '[&_a]:text-emerald-400 [&_a]:underline',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-zinc-600 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-500',
  '[&_p]:mb-2',
].join(' ')

export default function MarkdownEditor({ name, value, onChange, rows = 16, compact = false, required = false }) {
  const [tab, setTab]   = useState('edit')
  const textareaRef     = useRef(null)

  function fireChange(newValue) {
    onChange({ target: { name, value: newValue } })
  }

  function handleKeyDown(e) {
    if (!(e.metaKey || e.ctrlKey)) return
    const idx = SHORTCUTS[e.key.toLowerCase()]
    if (idx === undefined) return
    e.preventDefault()
    if (textareaRef.current) TOOLBAR_ACTIONS[idx].fn(textareaRef.current, fireChange)
  }

  const baseTextarea = 'w-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-y leading-relaxed'

  return (
    <div className="flex flex-col">
      {/* Tabs — only when not compact */}
      {!compact && (
        <div className="flex border-b border-zinc-700">
          {['edit', 'preview'].map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={[
                'px-4 py-1.5 text-xs font-mono transition-colors border-b-2 -mb-px',
                tab === t
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-zinc-500 hover:text-zinc-300 border-transparent',
              ].join(' ')}
            >
              {t === 'edit' ? 'Edit' : 'Preview'}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar — only in edit mode */}
      {tab === 'edit' && (
        <Toolbar textareaRef={textareaRef} value={value} onInsert={fireChange} />
      )}

      {/* Edit textarea */}
      {tab === 'edit' && (
        <textarea
          ref={textareaRef}
          name={name}
          value={value}
          onChange={e => fireChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={rows}
          required={required}
          className={`${baseTextarea} ${compact ? 'rounded' : 'rounded-b'}`}
        />
      )}

      {/* Preview */}
      {tab === 'preview' && (
        <div className="min-h-[120px] bg-zinc-800 border border-zinc-700 rounded-b px-3 py-3">
          {value.trim() === '' ? (
            <p className="text-zinc-600 text-xs font-mono italic">Nada que previsualizar.</p>
          ) : (
            <div className={PROSE}>
              <ReactMarkdown>{value}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
